import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import axios from "axios";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import Content from "./components/content.jsx";
import {subDays} from "date-fns";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import useReportFiltersFetch from "../../../hooks/use-report-filters-fetch.js";

const API_URL = import.meta.env.VITE_API_URL;

const initialFilters = () => ({
    dtStart: subDays(new Date(), 7),
    dtEnd: new Date(),
    hrStart: '00',
    hrEnd: '23',
    period: { label: "Todos", value: 'all' },
    pcl: "",
    vehicleTypes: [],
    lanes: []
});

const filterLabels = {
    dtStart: "Data inicial",
    dtEnd: "Data final",
    hrStart: "Hora inicial",
    hrEnd: "Hora final",
    period: "Período",
    pcl: "Ponto de coleta",
    vehicleTypes: "Tipo de veículo",
    lanes: "Câmeras"
};

const TrafficFlowPoint = () => {
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [filters, setFilters] = useState(initialFilters);
    const [dataLength, setDataLength] = useState(0)
    const { trafficFlowPointVehicleTypes } = useReportFiltersFetch({ fetchTrafficFlowPointVehicleTypes: true });

    useEffect(() => {
        setFilters(prev => ({...prev, vehicleTypes: trafficFlowPointVehicleTypes}))
    }, [trafficFlowPointVehicleTypes]);

    const [fixedTop, setFixedTop] = useState(null);
    const buttonRef = useRef(null);

    const title = 'Fluxo por ponto';
    const [headerSelected, setHeaderSelected] = useState(-1);

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : "",
            }, filterLabels)
        );

    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 1, 'month');

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setLoading(false);
                return;
            }

            if (!ReportUtils.validateHourOnly(filters.hrStart, filters.hrEnd, setLoading)) return;
            if (!ReportUtils.requiredFilterValidate(filters.period, "Período", setLoading)) return;
            if (!ReportUtils.requiredFilterValidate(filters.pcl, "Ponto de coleta", setLoading)) return;

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : null,
                hrStart: filters.hrStart ?? null,
                hrEnd: filters.hrEnd ?? null,
                pcl: filters.pcl?.value ?? '',
                period: filters.period.value ?? 'all',
                vehicleTypes: filters.vehicleTypes.length > 0 ? filters.vehicleTypes?.map(i => i.value) : trafficFlowPointVehicleTypes.map(i => i.value),
                lanes: filters.lanes ? filters.lanes?.map(i => i.value) : [],
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd},
            filterLabels, true);

            const { data: response } = await axios.post(API_URL + "/traffic_flow_point/apply", {
                ...formattedFilters,
                filters: auditFilters
            });

            const data = response.data;

            setReportData(data);
            setDataLength(data?.accumulatedDatasets.length || data?.averageDatasets.length || data?.barChartsDatasets.length || data?.passagesByDayDatasets.length || data?.passagesDatasets.length || 0)

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setLoading(false);
        }
    }

    useGlobalKey("Enter", apply);

    const clearSearch = () => {
        setFilters(prev => ({
            ...initialFilters(),
            vehicleTypes: trafficFlowPointVehicleTypes
        }));
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }

        setCollapsed(prev => !prev);
    }

    const printComponentRef = useRef(null);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className={"flex"}>

                <Template isLoading={loading}>
                    <Template.Header>
                        <div className="flex justify-between w-full">
                            <Template.Title>{title}</Template.Title>
                            <HeaderButtons
                                exportable={true}
                                exportInSheets={true}
                                printable={true}
                                headerChange={true}
                                printComponentRef={printComponentRef}
                                reportData={reportData}
                                data={dataLength}
                                headerSelected={headerSelected}
                                setHeaderSelected={setHeaderSelected}
                            />
                        </div>
                    </Template.Header>
                    <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
                        <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                              collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

                            <Filters setFilters={setFilters} filters={filters} trafficFlowPointVehicleTypes={trafficFlowPointVehicleTypes}/>
                            <AutoDatetime setFilters={setFilters}/>

                        </Menu>
                    </Template.Body>
                </Template>

                <Card className={`w-full h-fit mr-2 ${!reportData || dataLength === 0 ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || dataLength === 0 ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || dataLength === 0 ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
                            <NotAppliedContent reportData={reportData}/>
                            <NoDataContent reportData={reportData} data={dataLength}/>
                            <Content reportData={reportData} data={dataLength} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
                        </div>
                    </Card.Body>
                </Card>

            </div>
        </SubScroll>
    );
};

export default TrafficFlowPoint;